import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { UploadCloud, Trash2, Download, X } from 'lucide-react';
import { AuthContext } from '@context';
import axios_api from '@utils/axios_api';
import { toast } from 'react-toastify';

const CARDS = [
  {
    id: 2,
    label: '회사지침',
    staticImgSrc: null,
    storageKey: 'guide',
    docType: 'GUIDE',
    targetCompCode: null,
    adminRole: 'company',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700 text-white',
  },
  {
    id: 3,
    label: '조직도',
    staticImgSrc: null,
    storageKey: 'org',
    docType: 'ORG',
    targetCompCode: null,
    adminRole: 'company',
    btnClass: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  },
];

const useWindowSize = () => {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const update = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return size;
};

const InfoTabSection = () => {
  const { auth } = useContext(AuthContext);
  const [uploadedImages, setUploadedImages] = useState({});
  const [uploadError, setUploadError] = useState({});
  const [notReady, setNotReady] = useState({});
  const fileInputRefs = useRef({});
  const previewObjectUrlRef = useRef(null);
  const [preview, setPreview] = useState({ open: false, title: '', url: '', downloadSrc: '', loading: false });
  const { w: vw, h: vh } = useWindowSize();

  const isSystemAdmin = auth?.role === 'ROLE_SUPER_ADMIN' || auth?.role === 'ROLE_ADMIN';
  const isCompanyAdmin = auth?.role === 'ROLE_COMP_ADMIN';
  const compCode = auth?.compCode || 'default';


  const getImageSrc = (card) => uploadedImages[card.id] || card.staticImgSrc || null;

  const canManage = (card) =>
    (card.adminRole === 'system' && isSystemAdmin) ||
    (card.adminRole === 'company' && isCompanyAdmin);

  const handleFileChange = async (e, card) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setUploadError((p) => ({ ...p, [card.id]: '파일 크기는 5MB 이하여야 합니다.' }));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setUploadError((p) => ({ ...p, [card.id]: '이미지 파일만 업로드 가능합니다.' }));
      return;
    }
    setUploadError((p) => ({ ...p, [card.id]: '' }));

    const formData = new FormData();
    formData.append('compCode', card.targetCompCode || compCode);
    formData.append('docType', card.docType);
    formData.append('file', file);

    try {
      const res = await axios_api.post('/comp/doc/upload', formData);
      const doc = res.data.result;
      if (doc) {
        const fileIdKey = card.storageKey === 'law' ? 'lawFileId' : card.storageKey === 'guide' ? 'guideFileId' : 'orgFileId';
        if (doc[fileIdKey]) {
          const src = `${process.env.REACT_APP_BOOT_URL}/api/file/download?atchFileId=${doc[fileIdKey]}&fileSn=1&t=${Date.now()}`;
          setUploadedImages((p) => ({ ...p, [card.id]: src }));
          setNotReady((p) => ({ ...p, [card.id]: false }));
        }
      }
    } catch (err) {
      setUploadError((p) => ({ ...p, [card.id]: '업로드 중 서버 오류가 발생했습니다.' }));
    }
    e.target.value = '';
  };

  const handleDelete = async (card) => {
    if (!window.confirm(`${card.label} 이미지를 삭제하시겠습니까?`)) return;
    try {
      const tc = card.targetCompCode || compCode;
      await axios_api.delete(`/comp/doc/${tc}/${card.docType}`);
      setUploadedImages((p) => { const n = { ...p }; delete n[card.id]; return n; });
    } catch (err) {
      console.error('삭제 오류:', err);
    }
  };

  const closePreview = () => {
    if (previewObjectUrlRef.current) {
      window.URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    setPreview({ open: false, title: '', url: '', downloadSrc: '', loading: false });
  };

  useEffect(() => {
    if (!preview.open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [preview.open]);

  useEffect(() => () => closePreview(), []);

  const downloadAsBlob = async (src, filename) => {
    const isLikelyErrorBlob = (contentType) => {
      const ct = String(contentType || '').toLowerCase();
      return ct.includes('application/json') || ct.includes('text/html') || ct.includes('text/plain');
    };

    const throwMeaningfulBlobError = async (blob, statusCode) => {
      try {
        const text = await blob.text();
        try {
          const json = JSON.parse(text);
          const message = json?.message || json?.error || json?.msg;
          const error = new Error(message || 'FILE_NOT_AVAILABLE');
          error.status = statusCode || 404;
          error.details = json;
          throw error;
        } catch {
          const lowered = String(text).toLowerCase();
          const error = new Error('FILE_NOT_AVAILABLE');
          error.status = lowered.includes('404') || lowered.includes('not found') ? 404 : statusCode || 404;
          error.raw = text.slice(0, 300);
          throw error;
        }
      } catch (err) {
        if (err instanceof Error) throw err;
        const fallback = new Error('FILE_NOT_AVAILABLE');
        fallback.status = statusCode || 404;
        throw fallback;
      }
    };

    // Avoid blank tabs: download via Blob and fail gracefully on 404.
    let blob;
    let contentType = '';
    let statusCode = 200;

    if (typeof src === 'string' && src.startsWith('/')) {
      const res = await fetch(src);
      if (!res.ok) {
        const error = new Error(`HTTP ${res.status}`);
        error.status = res.status;
        throw error;
      }
      statusCode = res.status;
      contentType = res.headers.get('content-type') || '';
      blob = await res.blob();
    } else {
      const res = await axios_api.get(src, { responseType: 'blob', suppressToast: true });
      statusCode = res.status;
      contentType = res.headers?.['content-type'] || res.headers?.['Content-Type'] || '';
      blob = res.data;
    }

    // Some servers return 200 with HTML/JSON body for missing files. Prevent "downloading an error page".
    if (isLikelyErrorBlob(contentType)) {
      await throwMeaningfulBlobError(blob, statusCode);
    }

    if (!blob || blob.size === 0) {
      const error = new Error('EMPTY_FILE');
      error.status = 404;
      throw error;
    }

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const handlePreview = (card) => {
    const src = getImageSrc(card);
    if (!src) {
      toast.info('준비중입니다.');
      return;
    }

    setPreview({ open: true, title: card.label, url: '', downloadSrc: src, loading: true });

    // Static public asset: show directly.
    if (typeof src === 'string' && src.startsWith('/img/')) {
      setPreview({ open: true, title: card.label, url: src, downloadSrc: src, loading: false });
      return;
    }

    axios_api
      .get(src, { responseType: 'blob', suppressToast: true })
      .then(async (res) => {
        const contentType = res.headers?.['content-type'] || res.headers?.['Content-Type'] || '';
        if (String(contentType).toLowerCase().includes('application/json') || String(contentType).toLowerCase().includes('text/html')) {
          const text = await res.data.text();
          try {
            const json = JSON.parse(text);
            const message = json?.message || json?.error || json?.msg;
            const error = new Error(message || 'FILE_NOT_AVAILABLE');
            error.status = res.status || 404;
            throw error;
          } catch {
            const lowered = String(text).toLowerCase();
            const error = new Error('FILE_NOT_AVAILABLE');
            error.status = lowered.includes('404') || lowered.includes('not found') ? 404 : res.status || 404;
            throw error;
          }
        }

        if (!res.data || res.data.size === 0) {
          const error = new Error('EMPTY_FILE');
          error.status = 404;
          throw error;
        }

        const objectUrl = window.URL.createObjectURL(res.data);
        previewObjectUrlRef.current = objectUrl;
        setPreview({ open: true, title: card.label, url: objectUrl, downloadSrc: src, loading: false });
      })
      .catch((err) => {
        setUploadedImages((p) => {
          if (!p[card.id]) return p;
          const n = { ...p };
          delete n[card.id];
          return n;
        });

        closePreview();
        const status = err?.response?.status ?? err?.status;
        if (!status || status === 404) {
          toast.info('준비중입니다.');
        }
      });
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {CARDS.map((card) => {
          const manage = canManage(card);

          return (
            <div key={card.id} className="flex flex-col">
              {/* 카드 본문 — 에러/준비중 메시지 */}
              {manage && (notReady[card.id] || uploadError[card.id]) && (
                <div className="flex flex-col items-center justify-center px-6 py-4">
                  {notReady[card.id] && (
                    <p className="text-[14px] font-bold text-slate-400 animate-pulse">준비중입니다.</p>
                  )}
                  {uploadError[card.id] && (
                    <p className="text-[13px] font-bold text-red-500">{uploadError[card.id]}</p>
                  )}
                </div>
              )}

              {/* 하단 액션 */}
              <div className="px-4 py-4 mt-auto">
                <input
                  ref={(el) => (fileInputRefs.current[card.id] = el)}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, card)}
                />
                <button
                  type="button"
                  onClick={() => handlePreview(card)}
                  className={`w-full inline-flex items-center justify-center gap-3.5 rounded-2xl py-6 text-[20px] font-black transition-all duration-200 ${card.btnClass}`}
                >
                  <Download size={24} />
                  {`${String(card.label).replace(/\s+/g, "")}_다운로드`}
                </button>

                <div className="flex items-center justify-end gap-2 mt-3 min-h-[40px]">
                  {manage && (
                    <>
                      <button
                        onClick={() => fileInputRefs.current[card.id]?.click()}
                        className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-[14px] font-extrabold text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <UploadCloud size={16} />
                        {uploadedImages[card.id] ? '교체' : '저장'}
                      </button>
                      {uploadedImages[card.id] && (
                        <button
                          onClick={() => handleDelete(card)}
                          className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-4 py-2.5 text-[14px] font-extrabold text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 size={16} />
                          삭제
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {preview.open && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="미리보기"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePreview();
          }}
        >
          <div className="relative">
            <div className="sr-only">{preview.title}</div>

            {preview.loading ? (
              <div className="flex items-center justify-center rounded-xl bg-white/10 px-8 py-10 text-[15px] font-bold text-white/80 backdrop-blur"
                style={{ minWidth: Math.max(vw * 0.3, 160), minHeight: Math.max(vh * 0.25, 160) }}
              >
                불러오는 중...
              </div>
            ) : preview.url ? (
              <img
                src={preview.url}
                alt={preview.title}
                className="block rounded-xl object-contain shadow-2xl"
                style={{
                  maxWidth:  vw * 0.95,
                  maxHeight: vh * 0.92,
                  width: 'auto',
                  height: 'auto',
                }}
                onError={() => {
                  closePreview();
                  toast.info('준비중입니다.');
                }}
              />
            ) : null}

            {/* X 닫기 버튼 — 이미지 위 우상단 */}
            <button
              type="button"
              onClick={closePreview}
              className="absolute right-2 top-2 inline-flex items-center justify-center rounded-full bg-black/50 p-1.5 text-white backdrop-blur hover:bg-black/70 sm:p-2"
              aria-label="닫기"
            >
              <X size={18} />
            </button>

            {/* 다운로드 버튼 — 이미지 위 우하단 */}
            {!preview.loading && (
              <button
                type="button"
                onClick={() => {
                  const filename = `${String(preview.title).replace(/\\s+/g, '')}.png`;
                  downloadAsBlob(preview.downloadSrc, filename).catch((err) => {
                    const status = err?.response?.status ?? err?.status;
                    if (status === 404) {
                      toast.info('준비중입니다.');
                    }
                  });
                }}
                className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-xl bg-black/50 px-3 py-2 text-[13px] font-extrabold text-white backdrop-blur hover:bg-black/70 sm:gap-2 sm:px-4 sm:py-2.5 sm:text-[15px]"
              >
                <Download size={15} />
                다운로드
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InfoTabSection;
